import React, { useState, useEffect } from 'react';
import { User, Mail, Shield, Key, Calendar, CheckCircle, AlertCircle, X, Edit, Save, Store } from 'lucide-react';

const Profile = ({ auth, onPasswordReset, shopName, onShopNameUpdate }) => {
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // 'success' or 'error'
    const [isLoading, setIsLoading] = useState(false);
    const [isEditingShopName, setIsEditingShopName] = useState(false);
    const [newShopName, setNewShopName] = useState(shopName || '');

    const user = auth.currentUser;

    const handlePasswordReset = async () => {
        if (!user || !user.email) {
            setMessage('No authenticated user found or email is missing.');
            setMessageType('error');
            return;
        }

        setIsLoading(true);
        try {
            await onPasswordReset(user.email);
            setMessage('Password reset email sent successfully. Check your inbox.');
            setMessageType('success');
        } catch (error) {
            setMessage(error.message);
            setMessageType('error');
            console.error("Password reset error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleShopNameUpdate = async () => {
        if (!newShopName.trim()) {
            setMessage('Shop name cannot be empty');
            setMessageType('error');
            return;
        }

        setIsLoading(true);
        setMessage('');
        try {
            await onShopNameUpdate(newShopName.trim());
            setMessage('Shop name updated successfully!');
            setMessageType('success');
            setIsEditingShopName(false);
        } catch (error) {
            setMessage(error.message);
            setMessageType('error');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setNewShopName(shopName || '');
    }, [shopName]);

    const dismissMessage = () => {
        setMessage('');
        setMessageType('');
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'Not available';
        return new Date(timestamp).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (!user) {
        return (
            <div className="flex flex-col items-center justify-center min-h-96 text-center">
                <User className="h-16 w-16 text-gray-300 mb-4" />
                <h2 className="text-xl font-semibold text-gray-600 mb-2">No User Found</h2>
                <p className="text-gray-500">Please sign in to view your profile.</p>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-8 text-white">
                <div className="flex items-center space-x-4">
                    <div className="bg-white bg-opacity-20 rounded-full p-4">
                        <User className="h-12 w-12" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold">Profile</h1>
                        <p className="text-blue-100 mt-1">Manage your account settings and preferences</p>
                    </div>
                </div>
            </div>

            {/* Message Alert */}
            {message && (
                <div className={`relative p-4 rounded-lg border ${messageType === 'success'
                    ? 'bg-green-50 border-green-200 text-green-800'
                    : 'bg-red-50 border-red-200 text-red-800'
                    }`}>
                    <div className="flex items-start">
                        {messageType === 'success' ? (
                            <CheckCircle className="h-5 w-5 mt-0.5 mr-3 flex-shrink-0" />
                        ) : (
                            <AlertCircle className="h-5 w-5 mt-0.5 mr-3 flex-shrink-0" />
                        )}
                        <p className="flex-1">{message}</p>
                        <button
                            onClick={dismissMessage}
                            className="ml-3 flex-shrink-0 text-gray-400 hover:text-gray-600"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Account Information */}
                <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                            <User className="h-5 w-5 mr-2 text-blue-600" />
                            Account Information
                        </h2>
                    </div>
                    <div className="p-6 space-y-6">
                        {/* Shop Name */}
                        <div className="flex items-center space-x-4">
                            <div className="bg-purple-50 rounded-lg p-3">
                                <Store className="h-6 w-6 text-purple-600" />
                            </div>
                            <div className="flex-1">
                                <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                                    Shop Name
                                </label>
                                {isEditingShopName ? (
                                    <div className="flex items-center space-x-2 mt-1">
                                        <input
                                            type="text"
                                            value={newShopName}
                                            onChange={(e) => setNewShopName(e.target.value)}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Enter shop name"
                                        />
                                        <button
                                            onClick={handleShopNameUpdate}
                                            disabled={isLoading}
                                            className="p-2 text-green-600 hover:bg-green-100 rounded-md transition-colors"
                                        >
                                            <Save className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={() => {
                                                setIsEditingShopName(false);
                                                setNewShopName(shopName || '');
                                            }}
                                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-between mt-1">
                                        <p className="text-lg text-gray-900">{shopName || 'Not set'}</p>
                                        <button
                                            onClick={() => setIsEditingShopName(true)}
                                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-md transition-colors"
                                        >
                                            <Edit className="h-4 w-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Email */}
                        <div className="flex items-center space-x-4">
                            <div className="bg-blue-50 rounded-lg p-3">
                                <Mail className="h-6 w-6 text-blue-600" />
                            </div>
                            <div className="flex-1">
                                <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                                    Email Address
                                </label>
                                <p className="text-lg text-gray-900 mt-1">{user.email}</p>
                            </div>
                        </div>

                        {/* User ID */}
                        <div className="flex items-center space-x-4">
                            <div className="bg-gray-50 rounded-lg p-3">
                                <Shield className="h-6 w-6 text-gray-600" />
                            </div>
                            <div className="flex-1">
                                <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                                    User ID
                                </label>
                                <p className="text-sm text-gray-600 mt-1 font-mono bg-gray-50 px-2 py-1 rounded">
                                    {user.uid}
                                </p>
                            </div>
                        </div>

                        {/* Account Created */}
                        <div className="flex items-center space-x-4">
                            <div className="bg-green-50 rounded-lg p-3">
                                <Calendar className="h-6 w-6 text-green-600" />
                            </div>
                            <div className="flex-1">
                                <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                                    Account Created
                                </label>
                                <p className="text-lg text-gray-900 mt-1">
                                    {formatDate(user.metadata?.creationTime)}
                                </p>
                            </div>
                        </div>

                        {/* Last Sign In */}
                        <div className="flex items-center space-x-4">
                            <div className="bg-purple-50 rounded-lg p-3">
                                <Calendar className="h-6 w-6 text-purple-600" />
                            </div>
                            <div className="flex-1">
                                <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                                    Last Sign In
                                </label>
                                <p className="text-lg text-gray-900 mt-1">
                                    {formatDate(user.metadata?.lastSignInTime)}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Security Actions */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                            <Shield className="h-5 w-5 mr-2 text-blue-600" />
                            Security
                        </h2>
                    </div>
                    <div className="p-6">
                        <div className="space-y-4">
                            <div className="bg-gray-50 rounded-lg p-4">
                                <div className="flex items-center mb-3">
                                    <Key className="h-5 w-5 text-gray-600 mr-2" />
                                    <h3 className="font-medium text-gray-900">Password Reset</h3>
                                </div>
                                <p className="text-sm text-gray-600 mb-4">
                                    Send a password reset email to update your account password.
                                </p>
                                <button
                                    onClick={handlePasswordReset}
                                    disabled={isLoading}
                                    className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed"
                                >
                                    {isLoading ? (
                                        <div className="flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            Sending...
                                        </div>
                                    ) : (
                                        'Send Reset Email'
                                    )}
                                </button>
                            </div>

                            {/* <div className="bg-gray-50 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <Mail className="h-5 w-5 text-gray-600 mr-2" />
                                        <span className="font-medium text-gray-900">Email Verified</span>
                                    </div>
                                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${user.emailVerified
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-yellow-100 text-yellow-800'
                                        }`}>
                                        {user.emailVerified ? 'Verified' : 'Unverified'}
                                    </div>
                                </div>
                            </div> */}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;